import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';

import { RowFormula as RowFormulaModel } from '@futrli-sdk/predict';

import { useReportBuilderContext } from '../../../report-builder.context';
import { RowFormula } from '../../row-formula/row-formula';
import { SelectDataModal } from './select-data-modal';
import { SettingsAxisPanel } from './settings-axis-panel';
import { arrayMove } from '../../../utils-generic';
import { CARD_TYPES, DataField, WIDGET_TYPES } from '../../../types';
import { SettingsFinPanel } from './settings-fin-panel';
import { SettingsFinPanelNeedsMapping } from './settings-fin-panel-needsMapping';

import styles from './data-selector.scss';

export const DataSelector = ({ axisName, isSecondary }: { axisName: string; isSecondary?: boolean }) => {
  const {
    rowFormulaData,
    futrliFormulaData,
    currentCard,
    getSelectedFinFields,
    setSelectedFinFields,
    reportFieldsToMap,
    dataReferenceMapping,
    setDataRefsToMap,
    setDatasetsToMap,
    setMapTemplateDataModalOpen
  } = useReportBuilderContext();
  const selectedFinFields = getSelectedFinFields();
  const widgetType = currentCard?.[CARD_TYPES.WIDGET]?.widgetType as WIDGET_TYPES;
  const [isOpenRowFormulaDialog, setIsOpenRowFormulaDialog] = useState(false);
  const [activeFormula, setActiveFormula] = useState<RowFormulaModel | undefined>();
  const [showDataModal, setShowDataModal] = useState(false);

  const axisFields = useMemo(() => {
    return selectedFinFields.filter(
      f => !f.isSecondary === !isSecondary && (!f.isDeleted || reportFieldsToMap.find(m => m.ref === f.ref))
    );
  }, [isSecondary, selectedFinFields, reportFieldsToMap]);

  const openFormulaDialog = (id?: string) => {
    if (id) {
      let newActiveFormula = rowFormulaData?.filter(d => d.ref === id)[0];
      if (!newActiveFormula) {
        newActiveFormula = futrliFormulaData?.filter(d => d.ref === id)[0];
      }
      setActiveFormula(newActiveFormula);
    }
    setIsOpenRowFormulaDialog(true);
  };

  const handleCreateFormulaClick = () => {
    openFormulaDialog();
  };

  const handleCloseRowFormulaDialog = () => {
    setActiveFormula(undefined);
    setIsOpenRowFormulaDialog(false);
  };

  const handleShowDataModal = () => {
    setShowDataModal(true);
  };

  const handleCloseDataModal = () => {
    setShowDataModal(false);
  };

  const handleDeleteField = (ref: string) => {
    setSelectedFinFields(selectedFinFields.filter(f => f.ref !== ref));
  };

  const handleOnReorder = (result: DropResult) => {
    if (!result.destination) {
      return;
    }
    if (
      result.source.index !== null &&
      result.destination.index !== null &&
      result.destination.index !== result.source.index
    ) {
      let removedIndex = result.source.index;
      let addedIndex = result.destination.index;
      if (isSecondary) {
        removedIndex = selectedFinFields.findIndex(f => f.ref === axisFields[removedIndex].ref);
        addedIndex = selectedFinFields.findIndex(f => f.ref === axisFields[addedIndex].ref);
      }
      const newSelectedFinFields = arrayMove(selectedFinFields, removedIndex, addedIndex) as DataField[];
      setSelectedFinFields(newSelectedFinFields);
    }
  };

  const showFieldMapping = (ref: string) => {
    const fieldMappingData = dataReferenceMapping?.line_data_references?.[ref];
    if (fieldMappingData) {
      if (fieldMappingData.type === 'row_formula') {
        setActiveFormula({ ...fieldMappingData.data, name: fieldMappingData.name, ref } as RowFormulaModel);
        setIsOpenRowFormulaDialog(true);
      } else {
        setDataRefsToMap([ref]);
        setDatasetsToMap([]);
        setMapTemplateDataModalOpen(true);
      }
    }
  };

  const handleFormulaCreated = (f: RowFormulaModel, refToReplace?: string) => {
    if (f) {
      let existingFinFields = selectedFinFields;
      if (refToReplace) {
        existingFinFields = existingFinFields.filter(f => f.ref !== refToReplace);
      }
      setSelectedFinFields([
        ...existingFinFields,
        { ref: f.ref, name: f.name, classifier: 'row-formula', isSecondary }
      ]);
    }
  };

  return (
    <div className={clsx(styles.dataSelectionContainer)}>
      <div className={clsx(styles.actionButtons)}>
        <button onClick={() => handleShowDataModal()}>
          <FormattedMessage id="reporting.choose_data" defaultMessage="Choose data" />
        </button>
        <button onClick={handleCreateFormulaClick}>
          <FormattedMessage id="reporting.create_formula" defaultMessage="Create formula" />
        </button>
      </div>
      {widgetType !== WIDGET_TYPES.TABLE && <SettingsAxisPanel axis={axisName} isSecondary={isSecondary} />}
      <DragDropContext onDragEnd={handleOnReorder}>
        <Droppable droppableId="droppable" direction="vertical">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps} style={{ height: 'calc(100% - 500px)' }}>
              {axisFields.map((field, index) => (
                <Draggable draggableId={field.ref} index={index} key={field.ref}>
                  {provided => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={provided.draggableProps.style}
                    >
                      {reportFieldsToMap.find(f => f.ref === field.ref) ? (
                        <SettingsFinPanelNeedsMapping
                          field={field}
                          onDelete={handleDeleteField}
                          showFieldMapping={showFieldMapping}
                        />
                      ) : (
                        <SettingsFinPanel
                          field={field}
                          onDelete={handleDeleteField}
                          openFormulaDialog={openFormulaDialog}
                        />
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {isOpenRowFormulaDialog && (
        <RowFormula
          isOpenDialog={isOpenRowFormulaDialog}
          handleCloseDialog={handleCloseRowFormulaDialog}
          initialRowFormula={activeFormula}
          onFormulaCreated={handleFormulaCreated}
        />
      )}
      {showDataModal && (
        <SelectDataModal
          isOpenDialog={showDataModal}
          handleCloseDialog={handleCloseDataModal}
          openFormulaDialog={openFormulaDialog}
          isSecondary={isSecondary}
        />
      )}
    </div>
  );
};
